import { type ForwardedRef, forwardRef } from 'react';
import type { CSSProperties, HTMLAttributes } from 'react';
import { kaiFontWeight } from '../../../core/tokens';
import { kaiTypographyWeb } from '../../typography';
import { adminCommonColors } from './adminCommonColors';
import { resolveStyleOverride, type StyleOverride } from './styleOverrides';

export type CommonSegmentedToggleOption<Value extends string> = {
  value: Value;
  label: string;
  disabled?: boolean;
};

export const COMMON_SEGMENTED_TOGGLE_COLORS = {
  background: adminCommonColors.surface,
  border: adminCommonColors.border,
  selectedBackground: adminCommonColors.accentFill,
  selectedText: adminCommonColors.accentText,
  unselectedText: adminCommonColors.textSecondary,
} as const;

export const COMMON_SEGMENTED_TOGGLE_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  padding: 4,
  border: `0.5px solid ${COMMON_SEGMENTED_TOGGLE_COLORS.border}`,
  borderRadius: 5,
  background: COMMON_SEGMENTED_TOGGLE_COLORS.background,
};

const COMMON_SEGMENTED_TOGGLE_OPTION_STYLE: CSSProperties = {
  minWidth: 70,
  height: 24,
  border: 0,
  borderRadius: 5,
  padding: '4px 20px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: COMMON_SEGMENTED_TOGGLE_COLORS.background,
  color: COMMON_SEGMENTED_TOGGLE_COLORS.unselectedText,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  ...kaiTypographyWeb.xSmall,
  letterSpacing: '0.24px',
  appearance: 'none',
};

export const COMMON_SEGMENTED_TOGGLE_OPTION_SELECTED_STYLE: CSSProperties = {
  background: COMMON_SEGMENTED_TOGGLE_COLORS.selectedBackground,
  color: COMMON_SEGMENTED_TOGGLE_COLORS.selectedText,
  fontWeight: kaiFontWeight.bold,
};

export const COMMON_SEGMENTED_TOGGLE_OPTION_DISABLED_STYLE: CSSProperties = {
  cursor: 'not-allowed',
  opacity: 0.6,
};

export type CommonSegmentedToggleProps<Value extends string> =
  & {
    value: Value;
    options: readonly CommonSegmentedToggleOption<Value>[];
    onChange?: (value: Value) => void;
    optionCss?: StyleOverride;
    selectedOptionCss?: StyleOverride;
    disabledOptionCss?: StyleOverride;
    getOptionAriaLabel?: (option: CommonSegmentedToggleOption<Value>) => string;
    buttonType?: 'button' | 'submit' | 'reset';
  }
  & Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>
  & {
    styleOverride?: StyleOverride;
  };

const CommonSegmentedToggleComponent = <Value extends string>(
  {
    value,
    options,
    onChange,
    optionCss,
    selectedOptionCss,
    disabledOptionCss,
    getOptionAriaLabel,
    buttonType = 'button',
    styleOverride: rootCss,
    style,
    ...props
  }: CommonSegmentedToggleProps<Value>,
  ref: ForwardedRef<HTMLDivElement>,
) => {
  return (
    <div
      ref={ref}
      role='tablist'
      style={{
        ...COMMON_SEGMENTED_TOGGLE_STYLE,
        ...style,
        ...resolveStyleOverride(rootCss),
      }}
      {...props}
    >
      {options.map(option => {
        const isSelected = option.value === value;
        const isDisabled = option.disabled ?? false;

        return (
          <button
            key={option.value}
            type={buttonType}
            role='tab'
            aria-selected={isSelected}
            aria-label={getOptionAriaLabel?.(option) ?? option.label}
            disabled={isDisabled}
            style={{
              ...COMMON_SEGMENTED_TOGGLE_OPTION_STYLE,
              ...resolveStyleOverride(optionCss),
              ...(isSelected ? COMMON_SEGMENTED_TOGGLE_OPTION_SELECTED_STYLE : undefined),
              ...(isSelected ? resolveStyleOverride(selectedOptionCss) : undefined),
              ...(isDisabled ? COMMON_SEGMENTED_TOGGLE_OPTION_DISABLED_STYLE : undefined),
              ...(isDisabled ? resolveStyleOverride(disabledOptionCss) : undefined),
            }}
            onClick={() => {
              if (isDisabled || isSelected) return;
              onChange?.(option.value);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const CommonSegmentedToggleInner = forwardRef(CommonSegmentedToggleComponent);

CommonSegmentedToggleInner.displayName = 'CommonSegmentedToggle';

export const CommonSegmentedToggle = CommonSegmentedToggleInner as <Value extends string>(
  props: CommonSegmentedToggleProps<Value> & {
    ref?: ForwardedRef<HTMLDivElement>;
  },
) => ReturnType<typeof CommonSegmentedToggleComponent>;
